<template>
  <div class="dashboard-root min-h-screen">

    <!-- ═══════════════════════════════════════════════
         GLASSMORPHISM HEADER
    ════════════════════════════════════════════════ -->
    <header class="dash-header sticky top-0 z-50">
      <div class="dash-header-inner">
        <!-- Brand -->
        <div class="flex items-center gap-3">
          <div class="brand-dot"></div>
          <span class="brand-name">{{ organizationName || 'Muster' }}</span>
          <!-- <span class="brand-sep">·</span>
          <span class="brand-org">{{ authStore.user?.organizationName || 'Hospital' }}</span> -->
        </div>

        <!-- Right actions -->
        <div class="flex items-center gap-3">
          <span class="user-greeting">{{ authStore.user?.name }}</span>
          <button class="bell-btn" title="Notifications">
            <i class="pi pi-bell"></i>
            <span v-if="unreadCount > 0" class="bell-badge">{{ unreadCount }}</span>
          </button>
          <button class="logout-btn" @click="handleLogout" title="Logout">
            <i class="pi pi-sign-out"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="dash-body">

      <!-- ═══════════════════════════════════════════
           TODAY'S PULSE STRIP
      ════════════════════════════════════════════ -->
      <div class="pulse-strip">
        <span class="pulse-label">Today's Pulse</span>
        <button class="pulse-chip birthday" @click="scrollTo('section-people')">
          🎂
          <span class="pulse-chip-text">Birthdays</span>
          <span class="pulse-chip-count">{{ birthdays.length || '—' }}</span>
        </button>
        <button class="pulse-chip tasks-due" @click="scrollTo('section-tasks')">
          <i class="pi pi-clock"></i>
          <span class="pulse-chip-text">Tasks Due Today</span>
          <span class="pulse-chip-count">{{ tasksDueToday }}</span>
        </button>
        <button class="pulse-chip circulars" @click="scrollTo('section-circulars')">
          <i class="pi pi-file-edit"></i>
          <span class="pulse-chip-text">New Circulars</span>
          <span class="pulse-chip-count">{{ orgCirculars.length + deptCirculars.length }}</span>
        </button>
        <button class="pulse-chip news" @click="router.push('/news-events')">
          <i class="pi pi-megaphone"></i>
          <span class="pulse-chip-text">News &amp; Events</span>
          <span class="pulse-chip-count">{{ news.length }}</span>
        </button>
        <button class="pulse-chip sop" @click="router.push('/sop-documents')">
          <i class="pi pi-book"></i>
          <span class="pulse-chip-text">SOP Documents</span>
          <span class="pulse-chip-count">{{ sopCount }}</span>
        </button>
      </div>

      <!-- ═══════════════════════════════════════════
           SECTION 1 — WORKSPACE
      ════════════════════════════════════════════ -->
      <section class="dash-section">
        <div class="workspace-grid">

          <!-- Col A — Applications -->
          <div class="glass-card apps-col">
            <div class="card-header">
              <i class="pi pi-th-large text-teal-400"></i>
              <span>Applications</span>
            </div>

            <div v-if="loading" class="apps-skeleton-grid">
              <div v-for="n in 6" :key="n" class="skeleton-tile"></div>
            </div>

            <div v-else-if="error" class="empty-state">
              <i class="pi pi-exclamation-circle text-red-400 text-2xl"></i>
              <p class="text-sm text-red-300 mt-2">{{ error }}</p>
              <button class="btn-ghost mt-3" @click="loadModules">
                <i class="pi pi-refresh mr-1"></i> Retry
              </button>
            </div>

            <template v-else>
              <!-- Purchased modules -->
              <div v-if="accessibleModules.length" class="mb-4">
                <p class="section-label">Purchased</p>
                <div class="apps-grid">
                  <div
                    v-for="mod in accessibleModules"
                    :key="mod.id"
                    class="app-tile"
                    @click="handleModuleSelect(mod)"
                  >
                    <div class="app-icon" :style="{ background: `${mod.color}22` }">
                      <img :src="mod.icon" :alt="mod.name" class="w-5 h-5 object-contain" />
                    </div>
                    <span class="app-name">{{ mod.name }}</span>
                  </div>
                </div>
              </div>

              <!-- Other modules -->
              <div v-if="toBePurchasedModules.length">
                <p class="section-label text-slate-500">Other Modules</p>
                <div class="apps-grid opacity-50">
                  <div
                    v-for="mod in toBePurchasedModules"
                    :key="mod.id"
                    class="app-tile cursor-not-allowed"
                  >
                    <div class="app-icon" :style="{ background: `${mod.color}22` }">
                      <img :src="mod.icon" :alt="mod.name" class="w-5 h-5 object-contain" />
                    </div>
                    <span class="app-name">{{ mod.name }}</span>
                  </div>
                </div>
              </div>

              <div v-if="!accessibleModules.length && !toBePurchasedModules.length" class="empty-state">
                <i class="pi pi-box text-slate-500 text-2xl"></i>
                <p class="text-sm text-slate-400 mt-2">No applications found</p>
              </div>
            </template>
          </div>

          <!-- Col B — News & Events + Social Feeds -->
          <div class="glass-card news-col">
            <!-- News & Events -->
            <div class="card-header">
              <i class="pi pi-megaphone text-amber-400"></i>
              <span>News & Events</span>
              <button class="view-all-link ml-auto" @click="router.push('/news-events')">
                View All <i class="pi pi-arrow-right text-xs"></i>
              </button>
            </div>

            <!-- Category pills -->
            <div class="news-category-pills">
              <button
                v-for="cat in newsCategories"
                :key="cat"
                class="news-cat-pill"
                :class="{ active: selectedNewsCategory === cat }"
                @click="selectNewsCategory(cat)"
              >{{ cat }}</button>
            </div>

            <div v-if="news.length === 0" class="empty-state">
              <i class="pi pi-bell text-slate-500 text-xl"></i>
              <p class="text-xs text-slate-400 mt-1">No news available</p>
            </div>
            <ul v-else class="news-list">
              <li v-for="(item, i) in news" :key="i" class="news-item" style="cursor:pointer" @click="openNews(item)">
                <div class="flex gap-2 flex-wrap items-start">
                  <span class="news-type-badge" :class="item.Type == 1 ? 'badge-news' : 'badge-event'">
                    {{ item.Type == 1 ? 'News' : 'Event' }}
                  </span>
                  <span v-if="item.Category && item.Category !== 'General'" class="news-cat-tag">
                    {{ item.Category }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="news-title">{{ item.Title }}</p>
                  <p class="news-date">{{ formatDate(item.EventDate) }}</p>
                  <p class="news-desc">{{ item.Description }}</p>
                </div>
              </li>
            </ul>

            <!-- Social Media Widget (TRANSTAN style feed) -->
            <div class="mt-4 social-widget">
              <div class="card-header">
                <i class="pi pi-share-alt text-blue-400"></i>
                <span>Social Media</span>
                <button class="social-cfg-btn" @click="showSocialAdd = !showSocialAdd" title="Add / Configure">
                  <i :class="showSocialAdd ? 'pi pi-times' : 'pi pi-cog'"></i>
                </button>
              </div>

              <!-- Add / configure form -->
              <div v-if="showSocialAdd" class="social-cfg-form">
                <p class="social-cfg-hint">Select a platform and paste your page URL</p>
                <div class="social-quick-btns">
                  <button class="sqb sqb-fb"  @click="prefillSocialAdd('facebook')"><i class="pi pi-facebook"></i> Facebook</button>
                  <button class="sqb sqb-ig"  @click="prefillSocialAdd('instagram')"><i class="pi pi-instagram"></i> Instagram</button>
                  <button class="sqb sqb-yt"  @click="prefillSocialAdd('youtube')"><i class="pi pi-youtube"></i> YouTube</button>
                  <button class="sqb sqb-tw"  @click="prefillSocialAdd('twitter')"><i class="pi pi-twitter"></i> Twitter</button>
                  <button class="sqb sqb-li"  @click="prefillSocialAdd('linkedin')"><i class="pi pi-linkedin"></i> LinkedIn</button>
                </div>
                <div class="social-cfg-row" v-if="newSocialUrl">
                  <input v-model="newSocialUrl" class="social-url-input" placeholder="e.g. https://www.facebook.com/yourpage" @keyup.enter="addSocialPage" />
                  <button class="social-add-ok" @click="addSocialPage"><i class="pi pi-check"></i></button>
                  <button class="social-add-cancel" @click="showSocialAdd = false; newSocialUrl = ''"><i class="pi pi-times"></i></button>
                </div>
              </div>

              <!-- Platform pill tabs -->
              <div v-if="socialPages.length" class="social-platform-tabs">
                <button
                  v-for="(page, i) in socialPages"
                  :key="i"
                  class="spt"
                  :class="[`spt-${page.Platform}`, { 'spt-active': activeSocialTab === i }]"
                  @click="activeSocialTab = i"
                >
                  <i :class="socialTileIcon(page.Platform)"></i>
                  <span>{{ socialTileLabel(page.Platform) }}</span>
                  <span v-if="page._local" class="spt-remove" @click.stop="removeSocialPage(i)">×</span>
                </button>
              </div>

              <!-- Live feed iframe — Facebook Page Plugin with full like/comment interaction -->
              <div v-if="socialPages.length && socialPages[activeSocialTab]" class="social-feed-wrap">
                <iframe
                  :key="socialPages[activeSocialTab].EmbedUrl"
                  :src="socialPages[activeSocialTab].EmbedUrl"
                  class="social-feed-iframe"
                  frameborder="0"
                  scrolling="yes"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowfullscreen
                ></iframe>
              </div>

              <!-- Empty state -->
              <div v-if="!socialPages.length && !showSocialAdd" class="social-empty-state">
                <div class="social-empty-icons">
                  <span class="sei sei-fb"><i class="pi pi-facebook"></i></span>
                  <span class="sei sei-ig"><i class="pi pi-instagram"></i></span>
                  <span class="sei sei-yt"><i class="pi pi-youtube"></i></span>
                  <span class="sei sei-tw"><i class="pi pi-twitter"></i></span>
                </div>
                <p>Connect your social media pages</p>
                <button class="social-connect-btn" @click="showSocialAdd = true">
                  <i class="pi pi-plus"></i> Add Social Page
                </button>
              </div>
            </div>
          </div>

          <!-- Col C — Quick Actions -->
          <div class="glass-card quick-col">
            <div class="card-header">
              <i class="pi pi-bolt text-purple-400"></i>
              <span>Quick Actions</span>
            </div>
            <div class="quick-actions-list">
              <button class="quick-action-item" @click="handleModuleSelect(getModuleByName('Task Management'))">
                <div class="qa-icon bg-blue-500/20"><i class="pi pi-check-square text-blue-400"></i></div>
                <span>View All Tasks</span>
              </button>
              <button class="quick-action-item" @click="router.push('/circulars')">
                <div class="qa-icon bg-teal-500/20"><i class="pi pi-file-edit text-teal-400"></i></div>
                <span>Circulars</span>
              </button>
              <button class="quick-action-item" @click="router.push('/sop-documents')">
                <div class="qa-icon bg-orange-500/20"><i class="pi pi-book text-orange-400"></i></div>
                <span>SOP Documents</span>
              </button>
              <button class="quick-action-item" @click="scrollTo('section-tasks')">
                <div class="qa-icon bg-emerald-500/20"><i class="pi pi-list text-emerald-400"></i></div>
                <span>Task Dashboard</span>
              </button>
              <button class="quick-action-item" @click="scrollTo('section-people')">
                <div class="qa-icon bg-pink-500/20"><i class="pi pi-users text-pink-400"></i></div>
                <span>People</span>
              </button>
            </div>

            <!-- Task Stats Summary -->
            <div v-if="taskStats" class="task-stats-mini mt-4">
              <div class="card-header mb-2">
                <i class="pi pi-chart-bar text-slate-400"></i>
                <span class="text-xs">Task Overview</span>
              </div>
              <div class="stat-row-mini">
                <div class="stat-mini inprogress">
                  <span class="stat-mini-num">{{ taskStats.InProgress ?? 0 }}</span>
                  <span class="stat-mini-lbl">In Progress</span>
                </div>
                <div class="stat-mini delayed">
                  <span class="stat-mini-num">{{ taskStats.Delayed ?? 0 }}</span>
                  <span class="stat-mini-lbl">Delayed</span>
                </div>
                <div class="stat-mini completed">
                  <span class="stat-mini-num">{{ taskStats.Completed ?? 0 }}</span>
                  <span class="stat-mini-lbl">Completed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════════════════════════════════════════
           SECTION 2 — CIRCULARS
      ════════════════════════════════════════════ -->
      <section id="section-circulars" class="dash-section">
        <div class="section-title-row">
          <h2 class="section-title"><i class="pi pi-file-edit mr-2"></i>Circulars</h2>
          <button class="view-all-btn" @click="router.push('/circulars')">
            View All <i class="pi pi-arrow-right text-xs"></i>
          </button>
        </div>
        <div class="circulars-grid">

          <!-- Org Circulars -->
          <div class="glass-card circular-org">
            <div class="card-header">
              <span class="circular-dot org"></span>
              <span>Organisation Circulars</span>
              <span class="count-badge bg-blue-500/20 text-blue-300">{{ orgCirculars.length }}</span>
            </div>
            <div v-if="orgCirculars.length === 0" class="empty-state">
              <i class="pi pi-inbox text-blue-500/40 text-2xl"></i>
              <p class="text-xs text-slate-400 mt-1">No circulars yet</p>
            </div>
            <ul v-else class="circular-list">
              <li v-for="(c, i) in orgCirculars" :key="i" class="circular-item" style="cursor:pointer" @click="openCircular(c)">
                <div class="circular-meta">
                  <p class="circular-title">{{ c.Title }}</p>
                  <p class="circular-date">{{ formatDate(c.PublishedDate || c.CreatedDate) }}</p>
                </div>
                <a v-if="c.FullAttachmentPath" :href="c.FullAttachmentPath" target="_blank"
                   class="circular-attach" title="Download attachment" @click.stop>
                  <i class="pi pi-paperclip"></i>
                </a>
              </li>
            </ul>
          </div>

          <!-- Dept Circulars -->
          <div class="glass-card circular-dept">
            <div class="card-header">
              <span class="circular-dot dept"></span>
              <span>Department Circulars</span>
              <ONEDropdown
                v-if="isAdmin"
                v-model="selectedDeptId"
                :options="deptOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="— Select Department —"
                :showClear="true"
                class="dept-filter-dropdown"
              />
              <span class="count-badge bg-emerald-500/20 text-emerald-300">{{ deptCirculars.length }}</span>
            </div>
            <div v-if="deptCirculars.length === 0" class="empty-state">
              <i class="pi pi-inbox text-emerald-500/40 text-2xl"></i>
              <p class="text-xs text-slate-400 mt-1">No department circulars</p>
            </div>
            <ul v-else class="circular-list">
              <li v-for="(c, i) in deptCirculars" :key="i" class="circular-item" style="cursor:pointer" @click="openCircular(c)">
                <div class="circular-meta">
                  <p class="circular-title">{{ c.Title }}</p>
                  <p class="circular-date">{{ formatDate(c.PublishedDate || c.CreatedDate) }}</p>
                  <p v-if="c.DepartmentName" class="circular-dept-name">{{ c.DepartmentName }}</p>
                </div>
                <a v-if="c.FullAttachmentPath" :href="c.FullAttachmentPath" target="_blank"
                   class="circular-attach" title="Download attachment" @click.stop>
                  <i class="pi pi-paperclip"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- ═══════════════════════════════════════════
           SECTION 3 — PEOPLE (BIRTHDAYS STUB)
      ════════════════════════════════════════════ -->
      <section id="section-people" class="dash-section">
        <div class="section-title-row">
          <h2 class="section-title"><i class="pi pi-users mr-2"></i>People</h2>
          <span v-if="birthdays.length" class="bday-total-badge">{{ birthdays.length }} in next 30 days</span>
        </div>
        <div class="glass-card bday-card-wide">
          <div class="card-header">
            <span>🎂</span>
            <span>Upcoming Birthdays</span>
          </div>
          <div v-if="birthdays.length === 0" class="empty-state py-6">
            <span class="text-3xl">🎂</span>
            <p class="text-sm text-slate-500 mt-2">No upcoming birthdays in the next 30 days</p>
          </div>
          <div v-else class="bday-scroll-track">
            <div
              v-for="(person, i) in birthdays.slice(0, 10)"
              :key="i"
              class="bday-person-card"
              :class="{ 'bday-today': person.daysUntil === 0 }"
            >
              <div class="bday-person-avatar" :class="'bday-color-' + (i % 6)">
                {{ getInitials(person.Name) }}
              </div>
              <p class="bday-person-name">{{ person.Name }}</p>
              <p class="bday-person-date">{{ formatBdayDate(person.DateOfBirth) }}</p>
              <span class="bday-person-badge" :class="bdayBadgeClass(person.daysUntil)">
                {{ bdayBadgeLabel(person.daysUntil) }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════════════════════════════════════════
           SECTION 4 — TASKS (DOMINANT)
      ════════════════════════════════════════════ -->
      <section id="section-tasks" class="dash-section">
        <div class="section-title-row">
          <h2 class="section-title"><i class="pi pi-list-check mr-2"></i>Task Dashboard</h2>
          <button class="view-all-btn" @click="router.push('/Task/tasks')">
            View All <i class="pi pi-arrow-right text-xs"></i>
          </button>
        </div>

        <!-- Stat Row — computed from loaded tasks, no extra API call -->
        <div class="stat-row">
          <div class="stat-card pending">
            <span class="stat-num">{{ taskStats.pending }}</span>
            <span class="stat-lbl">Pending</span>
          </div>
          <div class="stat-card inprogress">
            <span class="stat-num">{{ taskStats.inProgress }}</span>
            <span class="stat-lbl">In Progress</span>
          </div>
          <div class="stat-card delayed">
            <span class="stat-num">{{ taskStats.overdue }}</span>
            <span class="stat-lbl">Overdue</span>
          </div>
          <div class="stat-card total">
            <span class="stat-num">{{ taskStats.total }}</span>
            <span class="stat-lbl">Total</span>
          </div>
        </div>

        <!-- Tab panel -->
        <div class="glass-card task-panel">
          <!-- Tabs -->
          <div class="task-tabs">
            <button
              class="task-tab"
              :class="{ active: activeTaskTab === 'my' }"
              @click="switchTaskTab('my')"
            >
              <i class="pi pi-user mr-1"></i> My Tasks
            </button>
            <button
              class="task-tab"
              :class="{ active: activeTaskTab === 'dept' }"
              @click="switchTaskTab('dept')"
            >
              <i class="pi pi-building mr-1"></i> Dept Tasks
            </button>
          </div>

          <!-- Loading -->
          <!-- <div v-if="tasksLoading" class="p-6 text-center">
            <i class="pi pi-spin pi-spinner text-teal-400 text-2xl"></i>
          </div> -->

          <!-- ONEDataTable -->
          <div class="task-table-body">
            <ONEDataTable
              :value="sortedTasks"
              :columns="taskColumns"
              :loading="tasksLoading"
              :rows="10"
              :paginator="false"
              dataKey="Id"
              :bulkimport="false"
              :clearShow="false"
              :exportStatus="1"
              :rowHover="true"
              :stripedRows="true"
              size="small"
            >
              <template #body-TaskPriorityName="{ data }">
                <span class="priority-badge" :class="priorityClass(data.TaskPriorityName)">
                  {{ data.TaskPriorityName || 'Low' }}
                </span>
              </template>

              <template #body-TaskName="{ data }">
                <span class="task-name-cell">{{ data.TaskName || data.Name }}</span>
                <span v-if="data.AssignAgentName" class="task-assignee">
                  <i class="pi pi-user"></i> {{ data.AssignAgentName }}
                </span>
              </template>

              <template #body-DepartmentName="{ data }">
                <span v-if="data.DepartmentName" class="dept-chip">
                  <i class="pi pi-building"></i> {{ data.DepartmentName }}
                </span>
                <span v-else class="no-data">—</span>
              </template>

              <template #body-TaskStatusName="{ data }">
                <span class="status-chip" :class="statusClass(data.TaskStatusName)">
                  {{ data.TaskStatusName || 'Pending' }}
                </span>
              </template>

              <template #body-EndDate="{ data }">
                <span v-if="data.EndDate" class="due-cell" :class="dueCellClass(data.EndDate, data.TaskStatusName)">
                  <i class="pi pi-clock"></i> {{ timeRemaining(data.EndDate) }}
                </span>
                <span v-else class="no-data">—</span>
              </template>
            </ONEDataTable>

            <!-- Server-side Paginator -->
            <Paginator
              v-if="tasksTotalRecords > 10"
              :rows="10"
              :totalRecords="tasksTotalRecords"
              :first="(tasksCurrentPage - 1) * 10"
              @page="onTaskPageChange"
              class="task-paginator"
            />
          </div>
        </div>
      </section>

      <!-- ═══════════════════════════════════════════
           SECTION 5 — SOP DOCUMENTS
      ════════════════════════════════════════════ -->
      <section id="section-sop" class="dash-section">
        <div class="section-title-row">
          <h2 class="section-title"><i class="pi pi-book mr-2"></i>SOP Quick Access</h2>
          <button class="view-all-btn" @click="router.push('/sop-documents')">
            View All <i class="pi pi-arrow-right text-xs"></i>
          </button>
        </div>
        <div class="glass-card sop-panel">

          <!-- Search -->
          <div class="sop-search-row">
            <div class="sop-search-wrap">
              <i class="pi pi-search sop-search-icon"></i>
              <input
                v-model="sopSearchTerm"
                class="sop-search-input"
                placeholder="Search procedures, protocols…"
                @input="onSOPSearch"
              />
              <button v-if="sopSearchTerm" class="sop-clear" @click="clearSOPSearch">
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>

          <!-- Category filters -->
          <div class="category-pills">
            <button
              v-for="cat in sopCategories"
              :key="cat"
              class="cat-pill"
              :class="{ active: selectedCategory === cat }"
              @click="selectCategory(cat)"
            >
              {{ cat }}
            </button>
          </div>

          <!-- SOP list -->
          <div v-if="sopLoading" class="p-6 text-center">
            <i class="pi pi-spin pi-spinner text-teal-400 text-xl"></i>
          </div>
          <div v-else-if="filteredSOPs.length === 0" class="empty-state py-8">
            <i class="pi pi-file-pdf text-slate-500 text-2xl"></i>
            <p class="text-sm text-slate-400 mt-2">No SOPs found</p>
          </div>
          <div v-else class="sop-list">
            <div v-for="(sop, i) in filteredSOPs" :key="i" class="sop-item" style="cursor:pointer" @click="router.push(`/sop-documents/${sop.Id}`)">
              <div class="sop-icon">
                <i class="pi pi-file-pdf text-red-400"></i>
              </div>
              <div class="sop-info">
                <p class="sop-title">{{ sop.Title }}</p>
                <p class="sop-meta">
                  <span class="sop-cat-badge">{{ sop.Category }}</span>
                  <span v-if="sop.Version" class="text-slate-500 text-xs ml-2">{{ sop.Version.startsWith('v') ? sop.Version : 'v' + sop.Version }}</span>
                  <span v-if="sop.DepartmentName" class="text-slate-500 text-xs ml-2">{{ sop.DepartmentName }}</span>
                </p>
                <p v-if="sop.Description" class="sop-desc">{{ sop.Description }}</p>
              </div>
              <a
                v-if="sop.FullFilePath"
                :href="sop.FullFilePath"
                target="_blank"
                class="sop-download"
                title="Download"
                @click.stop
              >
                <i class="pi pi-download"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div><!-- /dash-body -->

    <!-- ═══════════════════════════════════════════
         FOOTER
    ════════════════════════════════════════════ -->
    <footer class="dash-footer">
      <span>Powered by <strong>ClinexGen</strong></span>
      <span class="footer-sep">|</span>
      <span class="footer-iitm">
        <i class="pi pi-building"></i>
        Incubated at <strong>IITM Research Park, Chennai</strong>
      </span>
    </footer>

    <!-- ── Right Pane: Circular Detail ──────────────────── -->
    <ONEDrawer v-model="showCircularPane" position="right" style="width:480px">
      <template #header>
        <div class="pane-header">
          <span class="pane-title">{{ selectedCircular?.Title }}</span>
          <span class="pane-type-badge" :class="selectedCircular?.CircularType === 2 ? 'badge-dept' : 'badge-org'">
            {{ selectedCircular?.CircularType === 2 ? 'Department' : 'Organisation' }}
          </span>
        </div>
      </template>
      <div v-if="selectedCircular" class="pane-body">
        <div class="pane-meta-row">
          <span v-if="selectedCircular.DepartmentName" class="pane-meta-chip"><i class="pi pi-building mr-1"></i>{{ selectedCircular.DepartmentName }}</span>
          <span class="pane-meta-chip"><i class="pi pi-calendar mr-1"></i>{{ formatDate(selectedCircular.PublishedDate) }}</span>
          <span v-if="selectedCircular.CreatedByName" class="pane-meta-chip"><i class="pi pi-user mr-1"></i>{{ selectedCircular.CreatedByName }}</span>
        </div>
        <p v-if="selectedCircular.Content" class="pane-summary">{{ selectedCircular.Content }}</p>
        <div v-if="selectedCircular.ContentHtml" class="pane-html" v-html="selectedCircular.ContentHtml"></div>
        <a v-if="selectedCircular.FullAttachmentPath" :href="selectedCircular.FullAttachmentPath" target="_blank" class="pane-download-btn">
          <i class="pi pi-download mr-2"></i>Download Attachment
        </a>
        <button class="pane-nav-btn" @click="router.push(`/circulars/${selectedCircular.Id}`); showCircularPane = false">
          <i class="pi pi-external-link mr-2"></i>Open Full Page
        </button>
      </div>
    </ONEDrawer>

    <!-- ── Right Pane: News & Event Detail ───────────────── -->
    <ONEDrawer v-model="showNewsPane" position="right" style="width:480px">
      <template #header>
        <div class="pane-header">
          <span class="pane-title">{{ selectedNews?.Title }}</span>
          <span class="pane-type-badge" :class="selectedNews?.Type == 1 ? 'badge-news' : 'badge-event'">
            {{ selectedNews?.Type == 1 ? 'News' : 'Event' }}
          </span>
        </div>
      </template>
      <div v-if="selectedNews" class="pane-body">
        <div class="pane-meta-row">
          <span v-if="selectedNews.Category" class="pane-meta-chip">{{ selectedNews.Category }}</span>
          <span class="pane-meta-chip"><i class="pi pi-calendar mr-1"></i>{{ formatDate(selectedNews.EventDate) }}</span>
        </div>
        <p class="pane-summary">{{ selectedNews.Description }}</p>
        <a v-if="selectedNews.AttachmentPath" :href="selectedNews.AttachmentPath" target="_blank" class="pane-download-btn">
          <i class="pi pi-download mr-2"></i>Download Attachment
        </a>
        <button class="pane-nav-btn" @click="router.push(`/news-events/${selectedNews.Id}`); showNewsPane = false">
          <i class="pi pi-external-link mr-2"></i>Open Full Page
        </button>
      </div>
    </ONEDrawer>

    <!-- Loading overlay for module switch -->
    <div v-if="loading" class="module-loading-overlay">
      <i class="pi pi-spin pi-spinner text-teal-400 text-3xl"></i>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Paginator from 'primevue/paginator'
import { ONEDataTable } from '@/components/primevuecomponents/data/ONEDataTable/ONEDataTable'
import type { ONEDataTableColumn } from '@/components/primevuecomponents/data/ONEDataTable/ONEDataTable'

import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { moduleService, type Module } from '../../services/moduleService'
import { useToast } from 'primevue/usetoast'
import moment from 'moment'
import MastersService from '@/services/inventory/MastersService'
import StoreService from '@/services/inventory/StoreService'
import {
  getOrgCirculars,
  getDeptCirculars,
  getDepartmentsByOrg,
  getSOPDocuments,
  getSOPDocumentsByCategory,
  searchSOPDocuments,
  getMyTasks,
  getDeptTasks,
  getUpcomingBirthdays,
  registerDeviceToken,
  unregisterDeviceToken,
  getDashboardUserContext,
  getNewsByOrg,
  getOrganizationById
} from '../../services/dashboardService'
import { moduleService as modSvc } from '../../services/moduleService'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// ─── State ───────────────────────────────────────────────────────────────────
const loading = ref(false)
const error = ref<string | null>(null)
const modules = ref<Module[]>([])
const organizationName = ref(authStore.user?.organizationName || '')

// News
const news = ref<any[]>([])
const selectedNewsCategory = ref('All')
const newsCategories = ['All', 'General', 'Clinical', 'HR', 'Operations', 'Emergency', 'Training', 'Policy']

// Circulars
const orgCirculars = ref<any[]>([])
const deptCirculars = ref<any[]>([])
const deptOptions = ref<{ label: string; value: number }[]>([])
const selectedDeptId = ref<number | null>(null)
const isAdmin = computed(() => authStore.isAdmin || authStore.user?.isAdmin || false)

// Social
const socialPages = ref<any[]>([])
const activeSocialTab = ref(0)
const showSocialAdd = ref(false)
const newSocialUrl = ref('')

// Birthdays
const birthdays = ref<any[]>([])

// Tasks
const activeTaskTab = ref<'my' | 'dept'>('my')
const tasksLoading = ref(false)
const myTasks = ref<any[]>([])
const deptTasksList = ref<any[]>([])
const myTasksPage = ref(1)
const deptTasksPage = ref(1)
const myTasksTotalRecords = ref(0)
const deptTasksTotalRecords = ref(0)
const tasksTotalRecords = computed(() => activeTaskTab.value === 'my' ? myTasksTotalRecords.value : deptTasksTotalRecords.value)
const tasksCurrentPage = computed(() => activeTaskTab.value === 'my' ? myTasksPage.value : deptTasksPage.value)
const currentTasks = computed(() => activeTaskTab.value === 'my' ? myTasks.value : deptTasksList.value)

// Stats computed from actual loaded tasks (no extra API call)
const taskStats = computed(() => {
  const tasks = myTasks.value
  const pending = tasks.filter(t => !isTaskCompleted(t)).length
  const inProgress = tasks.filter(t => (t.TaskStatusName || '').toLowerCase().includes('progress')).length
  const overdue = tasks.filter(t => isPastDue(t.EndDate) && !isTaskCompleted(t)).length
  return { pending, inProgress, overdue, total: tasks.length }
})

const sortedTasks = computed(() => {
  const priorityOrder = (name: string) => {
    const n = (name || '').toLowerCase()
    if (n === 'high' || n === 'critical' || n === 'urgent') return 0
    if (n === 'medium' || n === 'normal') return 1
    return 2
  }
  return [...currentTasks.value].sort((a, b) => {
    const aOver = isPastDue(a.EndDate) && !isTaskCompleted(a)
    const bOver = isPastDue(b.EndDate) && !isTaskCompleted(b)
    if (aOver !== bOver) return aOver ? -1 : 1
    const aPri = priorityOrder(a.TaskPriorityName)
    const bPri = priorityOrder(b.TaskPriorityName)
    if (aPri !== bPri) return aPri - bPri
    const aComp = isTaskCompleted(a) ? 1 : 0
    const bComp = isTaskCompleted(b) ? 1 : 0
    if (aComp !== bComp) return aComp - bComp
    if (a.EndDate && b.EndDate) {
      return moment(a.EndDate, ['DD-MM-YYYY', moment.ISO_8601]).valueOf()
           - moment(b.EndDate, ['DD-MM-YYYY', moment.ISO_8601]).valueOf()
    }
    return 0
  })
})

const taskColumns: ONEDataTableColumn[] = [
  { field: 'TaskPriorityName', header: 'Priority', sortable: true, width: '90px' },
  { field: 'TaskName', header: 'Task', sortable: true },
  { field: 'DepartmentName', header: 'Department', sortable: true, width: '140px' },
  { field: 'TaskStatusName', header: 'Status', sortable: true, width: '110px' },
  { field: 'EndDate', header: 'Due / Remaining', sortable: true, width: '140px' }
]

// SOP
const allSOPs = ref<any[]>([])
const filteredSOPs = ref<any[]>([])
const sopLoading = ref(false)
const sopSearchTerm = ref('')
const selectedCategory = ref('All')
const sopCategories = ['All', 'Clinical', 'HR', 'Pharmacy', 'Emergency', 'Admin', 'Compliance']

// Right pane detail views
const showCircularPane = ref(false)
const selectedCircular = ref<any>(null)
const showNewsPane = ref(false)
const selectedNews = ref<any>(null)

function openCircular(c: any) { selectedCircular.value = c; showCircularPane.value = true }
function openNews(item: any) { selectedNews.value = item; showNewsPane.value = true }

// Notifications
const unreadCount = ref(0)

// MoceroUI helpers (preserved)
let filteredMenus: any = ref([])
let menuarray: any = ref([])
let storeData = ref({
  status: '', Id: 0, Type: 'StoreId', storename: '', storebranchmapid: 0,
  branchid: 0, IsZeroBill: false, IsCamp: false, SiteId: 0
})

// ─── Computed ─────────────────────────────────────────────────────────────────
const accessibleModules = computed(() => modules.value.filter(m => m.isAccessible))
const toBePurchasedModules = computed(() => modules.value.filter(m => !m.isAccessible))
const tasksDueToday = computed(() => {
  const today = new Date().toISOString().slice(0, 10)
  return currentTasks.value.filter(t => {
    if (!t.EndDate) return false
    const d = t.EndDate.slice(0, 10)
    return d === today
  }).length
})
const sopCount = computed(() => allSOPs.value.length)

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  await Promise.allSettled([
    loadModules(),
    loadNews(),
    loadDashboardData()
  ])
  await registerFCMToken()
})

// ─── Data Loaders ─────────────────────────────────────────────────────────────
async function loadModules() {
  try {
    error.value = null
    loading.value = true
    const orgId = authStore.user?.organizationId
    if (!orgId) throw new Error('Organization ID not found.')
    const [mods, orgName] = await Promise.all([
      moduleService.getUserModules(orgId),
      organizationName.value?.trim() ? Promise.resolve(organizationName.value) : getOrganizationById(orgId)
    ])
    modules.value = mods
    if (orgName) organizationName.value = orgName
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load modules'
  } finally {
    loading.value = false
  }
}

async function loadNews(category?: string) {
  try {
    const ctx = getDashboardUserContext()
    if (ctx.orgId) {
      news.value = await getNewsByOrg(ctx.orgId, ctx.branchId, ctx.roleId, category)
    } else {
      // Fallback when org context not yet available
      const response = await moduleService.loadNews()
      news.value = response?.result ?? []
    }
  } catch { news.value = [] }
}

async function selectNewsCategory(cat: string) {
  selectedNewsCategory.value = cat
  await loadNews(cat === 'All' ? undefined : cat)
}

async function loadDashboardData() {
  const ctx = getDashboardUserContext()
  if (!ctx.orgId) return

  await Promise.allSettled([
    loadCirculars(ctx),
    loadSocialPages(ctx),
    loadBirthdays(ctx),
    loadMyTasks(ctx),
    loadSOPs(ctx)
  ])
}

async function loadCirculars(ctx: ReturnType<typeof getDashboardUserContext>) {
  // Load departments for admin dropdown
  if (ctx.isAdmin && deptOptions.value.length === 0) {
    const depts = await getDepartmentsByOrg(ctx.orgId)
    deptOptions.value = depts.map((d: any) => ({ label: d.Name, value: d.ID }))
  }

  const deptId = ctx.isAdmin ? selectedDeptId.value : ctx.departmentId
  const [org, dept] = await Promise.allSettled([
    getOrgCirculars(ctx.orgId, ctx.branchId, ctx.isAdmin),
    deptId
      ? getDeptCirculars(ctx.orgId, ctx.branchId, deptId, ctx.isAdmin)
      : Promise.resolve([])
  ])
  orgCirculars.value = org.status === 'fulfilled' ? (org.value ?? []) : []
  deptCirculars.value = dept.status === 'fulfilled' ? (dept.value ?? []) : []
}

watch(selectedDeptId, async () => {
  const ctx = getDashboardUserContext()
  const deptId = selectedDeptId.value
  deptCirculars.value = deptId
    ? (await getDeptCirculars(ctx.orgId, ctx.branchId, deptId, true)) ?? []
    : []
})

async function loadSocialPages(_ctx: ReturnType<typeof getDashboardUserContext>) {
  const cfgPages = (window as any).__RUNTIME_CONFIG__?.SOCIAL_PAGES
  const cfg = Array.isArray(cfgPages) ? cfgPages.filter((p: any) => p?.EmbedUrl) : []
  const stored: any[] = JSON.parse(localStorage.getItem('one_social_pages') || '[]')
  socialPages.value = [...cfg, ...stored]
}

function toEmbedPage(rawUrl: string): { Platform: string; EmbedUrl: string; PageName: string; _local: true } {
  const url = rawUrl.trim()
  if (url.includes('instagram.com')) {
    const m = url.match(/instagram\.com\/([^/?#\s]+)/)
    const handle = m?.[1] ?? 'page'
    return { Platform: 'instagram', EmbedUrl: `https://www.instagram.com/${handle}/embed`, PageName: `@${handle}`, _local: true }
  }
  if (url.includes('facebook.com')) {
    const clean = url.replace(/\/$/, '')
    const encoded = encodeURIComponent(clean)
    const name = url.match(/facebook\.com\/([^/?#\s]+)/)?.[1] ?? 'Facebook'
    return { Platform: 'facebook', EmbedUrl: `https://www.facebook.com/plugins/page.php?href=${encoded}&tabs=timeline&width=500&height=420&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`, PageName: name, _local: true }
  }
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const m = url.match(/channel\/([^/?#]+)/) ?? url.match(/@([^/?#]+)/)
    const id = m?.[1] ?? ''
    return { Platform: 'youtube', EmbedUrl: id ? `https://www.youtube.com/embed?listType=user_uploads&list=${id}` : url, PageName: 'YouTube', _local: true }
  }
  if (url.includes('twitter.com') || url.includes('x.com')) {
    const m = url.match(/(?:twitter|x)\.com\/([^/?#\s]+)/)
    const handle = m?.[1] ?? 'page'
    return { Platform: 'twitter', EmbedUrl: `https://syndication.twitter.com/srv/timeline-profile/screen-name/${handle}?dnt=true`, PageName: `@${handle}`, _local: true }
  }
  return { Platform: 'other', EmbedUrl: url, PageName: 'Feed', _local: true }
}

function addSocialPage() {
  if (!newSocialUrl.value.trim()) return
  const page = toEmbedPage(newSocialUrl.value)
  const stored: any[] = JSON.parse(localStorage.getItem('one_social_pages') || '[]')
  stored.push(page)
  localStorage.setItem('one_social_pages', JSON.stringify(stored))
  socialPages.value = [...socialPages.value, page]
  activeSocialTab.value = socialPages.value.length - 1
  newSocialUrl.value = ''
  showSocialAdd.value = false
}

function removeSocialPage(idx: number) {
  const page = socialPages.value[idx]
  if (!page?._local) return
  socialPages.value.splice(idx, 1)
  const stored: any[] = JSON.parse(localStorage.getItem('one_social_pages') || '[]')
  localStorage.setItem('one_social_pages', JSON.stringify(stored.filter((p: any) => p.EmbedUrl !== page.EmbedUrl)))
  if (activeSocialTab.value >= socialPages.value.length) activeSocialTab.value = Math.max(0, socialPages.value.length - 1)
}

async function loadBirthdays(ctx: ReturnType<typeof getDashboardUserContext>) {
  birthdays.value = await getUpcomingBirthdays(ctx.orgId, 30)
}


async function loadMyTasks(ctx: ReturnType<typeof getDashboardUserContext>, page = 1) {
  tasksLoading.value = true
  try {
    const result = await getMyTasks(ctx.orgId, ctx.branchId, ctx.userId, ctx.isAdmin, ctx.deptRoleMapId, page)
    if (Array.isArray(result)) {
      myTasks.value = result
      myTasksTotalRecords.value = result[0]?.TotalRecords ?? result.length
      myTasksPage.value = page
    }
  } finally {
    tasksLoading.value = false
  }
}

async function loadDeptTasks(ctx: ReturnType<typeof getDashboardUserContext>, page = 1) {
  tasksLoading.value = true
  try {
    const result = await getDeptTasks(ctx.orgId, ctx.branchId, ctx.departmentId, ctx.userId, ctx.isAdmin, ctx.deptRoleMapId, page)
    if (Array.isArray(result)) {
      deptTasksList.value = result
      deptTasksTotalRecords.value = result[0]?.TotalRecords ?? result.length
      deptTasksPage.value = page
    }
  } finally {
    tasksLoading.value = false
  }
}

async function loadSOPs(ctx: ReturnType<typeof getDashboardUserContext>) {
  sopLoading.value = true
  try {
    allSOPs.value = await getSOPDocuments(ctx.orgId, ctx.branchId)
    filteredSOPs.value = allSOPs.value
  } finally {
    sopLoading.value = false
  }
}

// ─── Task Tab ─────────────────────────────────────────────────────────────────
async function switchTaskTab(tab: 'my' | 'dept') {
  activeTaskTab.value = tab
  const ctx = getDashboardUserContext()
  if (tab === 'my') await loadMyTasks(ctx, 1)
  else await loadDeptTasks(ctx, 1)
}

async function onTaskPageChange(event: { page: number; rows: number }) {
  const page = event.page + 1 // PrimeVue Paginator is 0-indexed
  const ctx = getDashboardUserContext()
  if (activeTaskTab.value === 'my') await loadMyTasks(ctx, page)
  else await loadDeptTasks(ctx, page)
}

// ─── SOP Search & Filter ─────────────────────────────────────────────────────
let sopSearchTimer: ReturnType<typeof setTimeout> | null = null

function onSOPSearch() {
  if (sopSearchTimer) clearTimeout(sopSearchTimer)
  sopSearchTimer = setTimeout(applySOPFilter, 400)
}

async function applySOPFilter() {
  const ctx = getDashboardUserContext()
  sopLoading.value = true
  try {
    if (sopSearchTerm.value.trim().length >= 2) {
      filteredSOPs.value = await searchSOPDocuments(ctx.orgId, ctx.branchId, sopSearchTerm.value.trim())
    } else if (selectedCategory.value && selectedCategory.value !== 'All') {
      filteredSOPs.value = await getSOPDocumentsByCategory(ctx.orgId, ctx.branchId, selectedCategory.value)
    } else {
      filteredSOPs.value = allSOPs.value
    }
  } finally {
    sopLoading.value = false
  }
}

function clearSOPSearch() {
  sopSearchTerm.value = ''
  applySOPFilter()
}

async function selectCategory(cat: string) {
  selectedCategory.value = cat
  sopSearchTerm.value = ''
  await applySOPFilter()
}

// ─── FCM Token Registration ───────────────────────────────────────────────────
async function registerFCMToken() {
  try {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return

    // FCM web push requires firebase SDK — for now we store the token placeholder
    // Full firebase SDK integration can be added when FCM config is provided
    const ctx = getDashboardUserContext()
    if (ctx.userId && ctx.orgId) {
      // Placeholder: replace with actual Firebase getToken() call
      const webToken = localStorage.getItem('fcmWebToken')
      if (webToken) {
        await registerDeviceToken({
          UserId: ctx.userId,
          DeviceToken: webToken,
          Platform: 'web',
          OrganizationId: ctx.orgId,
          BranchId: ctx.branchId,
          CreatedBy: ctx.userId
        })
      }
    }
  } catch { /* Silent — FCM is non-critical */ }
}

// ─── Module Navigation (preserved from original) ──────────────────────────────
async function handleModuleSelect(module: Module | undefined) {
  if (!module) return
  try {
    if (!module.isAccessible) return
    loading.value = true

    const roleId = authStore.user?.roleId
    if (!roleId) throw new Error('Role ID not found')
    if (!module.id || typeof module.id !== 'number') throw new Error('Invalid module ID')

    authStore.setActiveModule({ id: module.id, name: module.name, rights: [], menu: { menus: [], subMenus: [] } })

    if (module.type === 'moceroui') {
      await handleMoceroUIModule(module)
    } else {
      await handleONEUIModule(module, roleId)
    }

    toast.add({ severity: 'success', summary: 'Success', detail: `Switched to ${module.name}`, life: 3000 })
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err instanceof Error ? err.message : 'Failed', life: 3000 })
  } finally {
    loading.value = false
  }
}

async function handleMoceroUIModule(module: Module) {

  if(module.id === 3) {
     module.Path = await LoadMenus(authStore.user?.roleId, authStore.user?.organizationId, authStore.user?.id, authStore.user?.admin, authStore.user?.branchId)
  }

  await moduleService.prepareSessionForMoceroUI(module)
  moduleService.navigateToMoceroUI(module)
}

async function handleONEUIModule(module: Module, roleId: number) {
  await moduleService.getModuleRights(roleId, module.id)
  if (module.id === 10) {
    await router.push({ path: '/Task' })
  } else {
    await router.push({ path: '/ONELayout', query: { moduleId: module.id.toString() } })
  }
}

function getModuleByName(name: string): Module | undefined {
  return modules.value.find(m => m.name.toLowerCase().includes(name.toLowerCase()))
}

// ─── Logout (preserved) ───────────────────────────────────────────────────────
const handleLogout = async () => {
  try {
    const ctx = getDashboardUserContext()
    if (ctx.userId) {
      await unregisterDeviceToken(ctx.userId, 'web', ctx.userId)
    }
    localStorage.removeItem('token')
    localStorage.removeItem('RolesData')
    localStorage.removeItem('UsersData')
    authStore.clearAuth()
    await router.push('/')
  } catch (e) {
    console.error(e)
  }
}

// ─── MoceroUI helpers (preserved from original) ───────────────────────────────
async function LoadMenus(RoleId: any, OrganizationId: any, UserId: any, Admin: any, BranchId: any) {
  let response: any = await MastersService.GetMenus(1, RoleId, OrganizationId)
  menuarray.value = response['result']
  const uniqueMenus = new Map()
  response['result'].forEach((item: any) => {
    const { MenuId, MenuName, MenuPath } = item
    if (!uniqueMenus.has(MenuId)) uniqueMenus.set(MenuId, { MenuName, MenuPath })
  })
  filteredMenus.value = Array.from(uniqueMenus, ([MenuId, { MenuName, MenuPath }]) => ({ MenuId, MenuName, MenuPath }))
  try {
    await LoadStore(authStore.user?.id, authStore.user?.organizationId, authStore.user?.admin, authStore.user?.branchId)
    let TabIndex = { Indexval: 0 }
    localStorage.setItem('TabIndex', JSON.stringify(TabIndex))
    localStorage.setItem('RequestSubTabIndex', JSON.stringify(TabIndex))
    return JSON.stringify({
      name: 'StoreTabDetails',
      query: {
        status: storeData.value.status, Id: storeData.value.Id, Type: 'StoreId',
        storename: storeData.value.storename, storebranchmapid: storeData.value.storebranchmapid,
        BranchId: storeData.value.branchid, IsZeroBill: String(storeData.value.IsZeroBill),
        IsCamp: String(storeData.value.IsCamp), SiteId: storeData.value.SiteId
      }
    })
  } catch { return '/Masters' }
}

async function LoadStore(UserId: any, OrganizationId: any, Admin: any, BranchId: any) {
  let result: any = await StoreService.StoreDropDownListByUserId(UserId, OrganizationId, Admin, BranchId)
  storeData.value.Id = result['result'][0]['Id']
  storeData.value.Type = result['result'][0]['Type']
  storeData.value.storebranchmapid = result['result'][0]['StoreBranchMapId']
  if (result['result'][0]['StoreTypeName'] === 'Main Store') storeData.value.status = '1'
  else if (result['result'][0]['StoreTypeName'] === 'Sub Store') storeData.value.status = '2'
  else storeData.value.status = '3'
  storeData.value.storename = result['result'][0]['Name']
  storeData.value.branchid = result['result'][0]['BranchId']
  storeData.value.IsZeroBill = result['result'][0]['IsZeroBill'] || false
  storeData.value.IsCamp = result['result'][0]['IsCamp'] || false
  storeData.value.SiteId = result['result'][0]['SiteId'] || 0
}

// ─── Utilities ────────────────────────────────────────────────────────────────
function formatDate(d: any) {
  if (!d) return ''
  // Try DD-MM-YYYY format first (API returns this), then fall back to ISO
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('MMM DD, YYYY') : moment(d).format('MMM DD, YYYY')
}

function isPastDue(d: any) {
  if (!d) return false
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.isBefore(moment(), 'day') : false
}

function isTaskCompleted(task: any) {
  const name = (task.TaskStatusName || task.Status || '').toLowerCase()
  return name.includes('complet') || name.includes('done') || name.includes('closed')
}

function timeRemaining(d: any) {
  if (!d) return '—'
  const due = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  if (!due.isValid()) return formatDate(d)
  const diff = due.startOf('day').diff(moment().startOf('day'), 'days')
  if (diff < 0) return `${Math.abs(diff)}d overdue`
  if (diff === 0) return 'Due today'
  if (diff === 1) return 'Tomorrow'
  return `${diff}d left`
}

function dueCellClass(d: any, statusName: string) {
  const name = (statusName || '').toLowerCase()
  if (name.includes('complet') || name.includes('done') || name.includes('closed')) return 'due-completed'
  const due = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  if (!due.isValid()) return ''
  const diff = due.startOf('day').diff(moment().startOf('day'), 'days')
  if (diff < 0) return 'due-overdue'
  if (diff <= 3) return 'due-soon'
  return 'due-ok'
}

function getInitials(name: string) {
  if (!name) return '?'
  return name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
}

function formatBdayDate(dob: string): string {
  if (!dob) return ''
  const parts = dob.split('-')
  if (parts.length < 2) return dob
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const day = parseInt(parts[0])
  const month = months[parseInt(parts[1]) - 1]
  return `${day} ${month}`
}

function bdayBadgeLabel(days: number): string {
  if (days === 0) return '🎂 Today'
  if (days <= 7) return 'This Week'
  return 'This Month'
}

function bdayBadgeClass(days: number): string {
  if (days === 0) return 'bday-badge-today'
  if (days <= 7) return 'bday-badge-tomorrow'
  return 'bday-badge-soon'
}

function priorityClass(name: string) {
  const n = (name || '').toLowerCase()
  if (n === 'high' || n === 'critical' || n === 'urgent') return 'priority-high'
  if (n === 'medium' || n === 'normal') return 'priority-medium'
  return 'priority-low'
}

function statusClass(name: string) {
  const n = (name || '').toLowerCase()
  if (n.includes('complet') || n.includes('done') || n.includes('closed')) return 'status-completed'
  if (n.includes('progress') || n.includes('active') || n.includes('ongoing')) return 'status-inprogress'
  return 'status-pending'
}

const ALL_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube']

const missingPlatforms = computed(() =>
  ALL_PLATFORMS.filter(p => !socialPages.value.some(s => s.Platform === p))
)

function socialIcon(platform: string) {
  const map: Record<string, string> = {
    facebook: 'pi pi-facebook',
    youtube: 'pi pi-youtube',
    instagram: 'pi pi-instagram',
    twitter: 'pi pi-twitter',
    linkedin: 'pi pi-linkedin'
  }
  return map[platform?.toLowerCase()] || 'pi pi-globe'
}

function socialTileIcon(platform: string) {
  return socialIcon(platform)
}

function socialTileLabel(platform: string) {
  const labels: Record<string, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    twitter: 'Twitter / X',
    youtube: 'YouTube'
  }
  return labels[platform?.toLowerCase()] ?? platform
}

function prefillSocialAdd(platform: string) {
  const hints: Record<string, string> = {
    instagram: 'https://www.instagram.com/',
    facebook: 'https://www.facebook.com/',
    youtube: 'https://www.youtube.com/@',
    twitter: 'https://twitter.com/'
  }
  newSocialUrl.value = hints[platform] ?? ''
  showSocialAdd.value = true
}

function scrollTo(id: string) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
/* ── Dashboard Root ─────────────────────────────────── */
.dashboard-root {
  background: var(--one-rule-soft);
  min-height: 100vh;
  color: #1e293b;
  font-family: 'Inter', system-ui, sans-serif;
}

/* ── Header ─────────────────────────────────────────── */
.dash-header {
  background: #ffffff;
  border-bottom: 1px solid var(--one-rule);
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.dash-header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #14b8a6, #0ea5e9);
  box-shadow: 0 0 8px #14b8a680;
}
.brand-name { font-weight: 700; font-size: 1rem; color: #0f172a; }
.brand-sep { color: #94a3b8; margin: 0 4px; }
.brand-org { font-size: 0.85rem; color: var(--one-muted); }
.user-greeting { font-size: 0.85rem; color: var(--one-muted); }
.bell-btn, .logout-btn {
  position: relative;
  width: 34px; height: 34px;
  border-radius: 8px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--one-muted);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
}
.bell-btn:hover { background: #e0f2fe; color: #0284c7; border-color: #bae6fd; }
.logout-btn:hover { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
.bell-badge {
  position: absolute; top: -4px; right: -4px;
  background: #ef4444; color: white;
  font-size: 9px; font-weight: 700;
  width: 16px; height: 16px;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
}

/* ── Body ───────────────────────────────────────────── */
.dash-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex; flex-direction: column; gap: 1.5rem;
}
.dash-section { display: flex; flex-direction: column; gap: 1rem; }
.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 1rem; font-weight: 600; color: #374151;
  display: flex; align-items: center;
  margin-bottom: 0 !important;
}
.view-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f0fdfa;
  border: 1px solid #5eead4;
  border-radius: 8px;
  padding: 5px 14px;
  font-size: 0.8rem;
  color: #0d9488;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.15s;
}
.view-all-btn:hover { background: #ccfbf1; }
.view-all-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 0.78rem;
  color: #0d9488;
  cursor: pointer;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 6px;
  transition: all 0.15s;
}
.view-all-link:hover { background: #f0fdfa; }

/* ── Glass Card ─────────────────────────────────────── */
.glass-card {
  background: #ffffff;
  border: 1px solid var(--one-rule);
  border-radius: 14px;
  padding: 1.25rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

/* ── Card Header ────────────────────────────────────── */
.card-header {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.875rem; font-weight: 600; color: #374151;
  margin-bottom: 1rem;
}
.section-label {
  font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.08em; color: #9ca3af; margin-bottom: 0.5rem;
}

/* ── Workspace Grid ─────────────────────────────────── */
.workspace-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 280px;
  gap: 1rem;
}
@media (max-width: 1024px) { .workspace-grid { grid-template-columns: 1fr 1fr; } .quick-col { grid-column: 1 / -1; } }
@media (max-width: 640px) { .workspace-grid { grid-template-columns: 1fr; } }

/* ── Apps Grid ──────────────────────────────────────── */
.apps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
}
.app-tile {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 10px 6px; border-radius: 12px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  cursor: pointer; transition: all 0.2s;
}
.app-tile:hover { background: #e0f2fe; border-color: #7dd3fc; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(2,132,199,0.12); }
.app-icon {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.app-name {
  font-size: 0.65rem; text-align: center; color: var(--one-muted);
  line-height: 1.3; word-break: break-word;
}
.apps-skeleton-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
}
.skeleton-tile {
  height: 72px; border-radius: 12px;
  background: linear-gradient(90deg, var(--one-rule-soft) 25%, var(--one-rule) 50%, var(--one-rule-soft) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Quick Actions ──────────────────────────────────── */
.quick-actions-list { display: flex; flex-direction: column; gap: 6px; }
.quick-action-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px;
  background: #f8fafc; border: 1px solid var(--one-rule);
  color: var(--one-muted); font-size: 0.8rem; cursor: pointer; transition: all 0.2s;
  text-align: left;
}
.quick-action-item:hover { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.qa-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* ── Task Stats Mini ────────────────────────────────── */
.stat-row-mini { display: flex; gap: 6px; }
.stat-mini {
  flex: 1; border-radius: 10px; padding: 8px 4px;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.stat-mini.inprogress { background: #eff6ff; }
.stat-mini.delayed { background: #fef2f2; }
.stat-mini.completed { background: #f0fdf4; }
.stat-mini-num { font-size: 1.1rem; font-weight: 700; color: #0f172a; }
.stat-mini-lbl { font-size: 0.6rem; color: var(--one-muted); text-align: center; }

/* ── News ───────────────────────────────────────────── */
.news-category-pills {
  display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 8px;
}
.news-cat-pill {
  font-size: 0.68rem; font-weight: 600; padding: 3px 10px; border-radius: 20px;
  border: 1px solid var(--one-rule); background: #f8fafc;
  color: var(--one-muted); cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.news-cat-pill:hover { color: #374151; background: var(--one-rule-soft); }
.news-cat-pill.active { background: #fef3c7; border-color: #fcd34d; color: #92400e; }

.news-list { display: flex; flex-direction: column; gap: 8px; max-height: 220px; overflow-y: auto; }
.news-item { display: flex; flex-direction: column; gap: 5px; padding: 8px 10px; border-radius: 8px; background: #f8fafc; border: 1px solid var(--one-rule); }
.news-type-badge {
  flex-shrink: 0; font-size: 0.6rem; font-weight: 700;
  padding: 2px 6px; border-radius: 4px; text-transform: uppercase;
}
.badge-news { background: #dbeafe; color: #1d4ed8; }
.badge-event { background: #ede9fe; color: #6d28d9; }
.news-cat-tag {
  font-size: 0.6rem; font-weight: 600; padding: 2px 6px; border-radius: 4px;
  background: #fef3c7; color: #92400e;
}
.news-title { font-size: 0.8rem; font-weight: 600; color: #1e293b; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; }
.news-date { font-size: 0.7rem; color: #9ca3af; margin-top: 2px; }
.news-desc { font-size: 0.75rem; color: var(--one-muted); margin-top: 3px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* ── Social Media Widget (TRANSTAN style) ───────────── */
.social-cfg-btn {
  margin-left: auto; background: none; border: 1px solid #cbd5e1; border-radius: 6px;
  color: var(--one-muted); padding: 3px 8px; cursor: pointer; font-size: 0.75rem;
  display: flex; align-items: center; transition: all 0.15s;
}
.social-cfg-btn:hover { background: var(--one-rule-soft); color: #0ea5e9; border-color: #7dd3fc; }

/* Config panel */
.social-cfg-form { background: #f8fafc; border: 1px solid var(--one-rule); border-radius: 10px; padding: 12px; margin-bottom: 10px; }
.social-cfg-hint { font-size: 0.7rem; color: var(--one-muted); margin-bottom: 8px; font-weight: 500; }
.social-quick-btns { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.sqb {
  display: flex; align-items: center; gap: 5px; padding: 5px 11px;
  border: none; border-radius: 6px; cursor: pointer; font-size: 0.72rem; font-weight: 600;
  transition: opacity 0.15s;
}
.sqb:hover { opacity: 0.85; }
.sqb-fb  { background: #1877f2; color: #fff; }
.sqb-ig  { background: linear-gradient(135deg, #f09433, #dc2743, #bc1888); color: #fff; }
.sqb-yt  { background: #ff0000; color: #fff; }
.sqb-tw  { background: #000; color: #fff; }
.social-cfg-row { display: flex; gap: 6px; align-items: center; margin-top: 6px; }
.social-url-input {
  flex: 1; font-size: 0.73rem; padding: 6px 10px; border-radius: 6px;
  border: 1px solid #cbd5e1; outline: none; color: #1e293b; background: #fff;
}
.social-url-input:focus { border-color: #7dd3fc; }
.social-add-ok   { border: none; border-radius: 6px; padding: 6px 10px; cursor: pointer; background: #0ea5e9; color: #fff; font-size: 0.8rem; }
.social-add-ok:hover { background: #0284c7; }
.social-add-cancel { border: none; border-radius: 6px; padding: 6px 10px; cursor: pointer; background: var(--one-rule); color: var(--one-muted); font-size: 0.8rem; }
.social-add-cancel:hover { background: #cbd5e1; }

/* Platform pill tabs */
.social-platform-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.spt {
  display: flex; align-items: center; gap: 5px; padding: 5px 13px;
  border-radius: 20px; border: 2px solid var(--one-rule); background: #f8fafc;
  color: var(--one-muted); font-size: 0.73rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.spt:hover { border-color: #94a3b8; }
.spt-active.spt-facebook  { background: #1877f2; color: #fff; border-color: #1877f2; }
.spt-active.spt-instagram { background: linear-gradient(135deg, #f09433, #dc2743, #bc1888); color: #fff; border-color: #dc2743; }
.spt-active.spt-youtube   { background: #ff0000; color: #fff; border-color: #ff0000; }
.spt-active.spt-twitter   { background: #000; color: #fff; border-color: #000; }
.spt-remove { margin-left: 4px; font-size: 0.9rem; font-weight: 700; opacity: 0.7; }
.spt-remove:hover { opacity: 1; }

/* Live feed frame — matches TRANSTAN plugin look */
.social-feed-wrap {
  border-radius: 12px; overflow: hidden;
  border: 1px solid var(--one-rule); box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  background: #fff;
}
.social-feed-iframe { width: 100%; height: 500px; border: none; display: block; }

/* Empty state */
.social-empty-state { text-align: center; padding: 20px 10px; }
.social-empty-icons { display: flex; justify-content: center; gap: 10px; margin-bottom: 12px; }
.sei {
  width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center;
  justify-content: center; font-size: 1.1rem; color: #fff;
}
.sei-fb { background: #1877f2; }
.sei-ig { background: linear-gradient(135deg, #f09433, #dc2743, #bc1888); }
.sei-yt { background: #ff0000; }
.sei-tw { background: #000; }
.social-empty-state p { font-size: 0.78rem; color: var(--one-muted); margin-bottom: 10px; font-weight: 500; }
.social-connect-btn {
  display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px;
  background: #0ea5e9; color: #fff; border: none; border-radius: 8px;
  font-size: 0.75rem; font-weight: 600; cursor: pointer;
}
.social-connect-btn:hover { background: #0284c7; }
.social-iframe { width: 100%; height: 280px; border: none; display: block; }

/* ── Circulars ──────────────────────────────────────── */
.circulars-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
@media (max-width: 640px) { .circulars-grid { grid-template-columns: 1fr; } }
.circular-org { border-top: 3px solid #3b82f6; }
.circular-dept { border-top: 3px solid #22c55e; }
.circular-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.circular-dot.org { background: #3b82f6; }
.circular-dot.dept { background: #22c55e; }
.count-badge {
  margin-left: auto; font-size: 0.65rem; font-weight: 700;
  padding: 2px 7px; border-radius: 20px;
}
.dept-filter-dropdown {
  margin-left: auto;
  max-width: 180px;
  flex-shrink: 0;
  font-size: 0.75rem;
}
.dept-filter-dropdown + .count-badge { margin-left: 8px; }

/* ── Right Pane Styles ──────────────────────────────── */
.pane-header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pane-title { font-size: 1rem; font-weight: 700; color: #0f172a; flex: 1; }
.pane-type-badge { font-size: 0.65rem; font-weight: 700; padding: 3px 8px; border-radius: 20px; white-space: nowrap; }
.badge-org { background: #dbeafe; color: #1d4ed8; }
.badge-dept { background: #dcfce7; color: #15803d; }
.badge-news { background: #dbeafe; color: #1d4ed8; }
.badge-event { background: #fef3c7; color: #b45309; }
.pane-body { display: flex; flex-direction: column; gap: 1rem; padding: 0.25rem 0; }
.pane-meta-row { display: flex; flex-wrap: wrap; gap: 6px; }
.pane-meta-chip { font-size: 0.72rem; background: var(--one-rule-soft); color: #475569; padding: 3px 8px; border-radius: 6px; display: inline-flex; align-items: center; }
.pane-summary { font-size: 0.875rem; color: #475569; line-height: 1.6; background: #f8fafc; border-radius: 8px; padding: 0.75rem; }
.pane-html { font-size: 0.875rem; color: #334155; line-height: 1.7; }
.pane-html h1, .pane-html h2, .pane-html h3 { font-weight: 700; margin: 0.5rem 0; }
.pane-html p { margin: 0.4rem 0; }
.pane-download-btn {
  display: inline-flex; align-items: center; padding: 0.5rem 1rem;
  background: #0d9488; color: #fff; border-radius: 8px; font-size: 0.8rem;
  font-weight: 600; text-decoration: none; width: fit-content;
}
.pane-download-btn:hover { background: #0f766e; }
.pane-nav-btn {
  display: inline-flex; align-items: center; padding: 0.5rem 1rem;
  background: var(--one-rule-soft); color: #334155; border: 1px solid var(--one-rule);
  border-radius: 8px; font-size: 0.8rem; font-weight: 600; cursor: pointer; width: fit-content;
}
.pane-nav-btn:hover { background: var(--one-rule); }

.circular-list { display: flex; flex-direction: column; gap: 8px; max-height: 260px; overflow-y: auto; }
.circular-item {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 10px; border-radius: 10px;
  background: #f8fafc; border: 1px solid var(--one-rule);
}
.circular-meta { flex: 1; min-width: 0; }
.circular-title { font-size: 0.8rem; font-weight: 600; color: #1e293b; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.circular-date { font-size: 0.7rem; color: #9ca3af; margin-top: 2px; }
.circular-dept-name { font-size: 0.7rem; color: var(--one-muted); }
.circular-attach {
  color: #94a3b8; font-size: 0.85rem; flex-shrink: 0;
  padding: 4px; border-radius: 6px; transition: all 0.15s;
}
.circular-attach:hover { color: #0284c7; background: #e0f2fe; }

/* ── Birthdays ──────────────────────────────────────── */
.bday-card-wide { width: 100%; }
.bday-total-badge {
  font-size: 0.72rem; font-weight: 600; padding: 3px 10px;
  background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  border-radius: 20px;
}
.bday-scroll-track {
  display: flex; gap: 12px;
  overflow-x: auto; padding-bottom: 6px;
  scrollbar-width: thin; scrollbar-color: var(--one-rule) transparent;
}
.bday-scroll-track::-webkit-scrollbar { height: 4px; }
.bday-scroll-track::-webkit-scrollbar-track { background: transparent; }
.bday-scroll-track::-webkit-scrollbar-thumb { background: var(--one-rule); border-radius: 4px; }
.bday-person-card {
  flex-shrink: 0; width: 130px;
  background: #f8fafc; border: 1px solid var(--one-rule); border-radius: 14px;
  padding: 16px 10px 12px;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  text-align: center; transition: box-shadow 0.15s;
}
.bday-person-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.bday-person-card.bday-today {
  background: #fdf4ff; border-color: #e9d5ff;
  box-shadow: 0 0 0 2px #e9d5ff;
}
.bday-person-avatar {
  width: 52px; height: 52px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 700; color: white; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.bday-color-0 { background: linear-gradient(135deg, #ec4899, #8b5cf6); }
.bday-color-1 { background: linear-gradient(135deg, #3b82f6, #06b6d4); }
.bday-color-2 { background: linear-gradient(135deg, #10b981, #3b82f6); }
.bday-color-3 { background: linear-gradient(135deg, #f59e0b, #ef4444); }
.bday-color-4 { background: linear-gradient(135deg, #8b5cf6, #ec4899); }
.bday-color-5 { background: linear-gradient(135deg, #06b6d4, #10b981); }
.bday-person-name {
  font-size: 0.75rem; font-weight: 700; color: #1e293b;
  line-height: 1.3; word-break: break-word; margin: 0;
}
.bday-person-date {
  font-size: 0.68rem; color: var(--one-muted); margin: 0;
}
.bday-person-badge {
  font-size: 0.65rem; font-weight: 600; padding: 2px 8px;
  border-radius: 20px; white-space: nowrap;
}
.bday-badge-today   { background: #fce7f3; color: #be185d; }
.bday-badge-tomorrow { background: #ede9fe; color: #6d28d9; }
.bday-badge-soon    { background: #e0f2fe; color: #0369a1; }

/* ── Task Section ───────────────────────────────────── */
.stat-row { display: flex; gap: 10px; flex-wrap: wrap; }
.stat-card {
  flex: 1; min-width: 100px; border-radius: 12px; padding: 14px 16px;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  border: 1px solid var(--one-rule);
}
.stat-card.pending { background: #fefce8; border-color: #fde68a; }
.stat-card.inprogress { background: #eff6ff; border-color: #bfdbfe; }
.stat-card.delayed { background: #fef2f2; border-color: #fecaca; }
.stat-card.total { background: #f8fafc; border-color: var(--one-rule); }
.stat-num { font-size: 1.6rem; font-weight: 800; color: #0f172a; }
.stat-lbl { font-size: 0.72rem; color: var(--one-muted); text-transform: uppercase; letter-spacing: 0.05em; }

.task-panel { padding: 0; overflow: hidden; }
.task-table-body { padding: 0; }
.task-paginator { border-top: 1px solid var(--one-rule); padding: 8px 12px; background: #f8fafc; }
.task-tabs {
  display: flex; border-bottom: 1px solid var(--one-rule);
}
.task-tab {
  flex: 1; padding: 12px; font-size: 0.85rem; font-weight: 600;
  color: var(--one-muted); border: none; background: transparent; cursor: pointer;
  transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 4px;
}
.task-tab.active { color: #0d9488; border-bottom: 2px solid #0d9488; }
.task-tab:hover { color: #374151; background: #f8fafc; }

/* ── Task Table ─────────────────────────────────────── */
.stat-section-head {
  margin-bottom: 6px;
}
.stat-section-label {
  font-size: 0.72rem; font-weight: 600; color: var(--one-muted);
  text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 4px;
}

.priority-badge {
  font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;
  white-space: nowrap; display: inline-block;
}
.priority-high { background: #fee2e2; color: #dc2626; }
.priority-medium { background: #ffedd5; color: #ea580c; }
.priority-low { background: #dbeafe; color: #1d4ed8; }

.status-chip {
  font-size: 0.65rem; font-weight: 600; padding: 2px 7px; border-radius: 4px;
  white-space: nowrap; display: inline-block;
}
.status-pending { background: var(--one-rule-soft); color: var(--one-muted); }
.status-inprogress { background: #dbeafe; color: #1d4ed8; }
.status-completed { background: #dcfce7; color: #15803d; }


.task-name-cell {
  display: block; font-weight: 600; color: #1e293b;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 280px;
}
.task-assignee {
  display: block; font-size: 0.7rem; color: #94a3b8; margin-top: 2px;
}
.task-assignee .pi { font-size: 0.65rem; }

.dept-chip {
  font-size: 0.72rem; color: #475569; display: flex; align-items: center; gap: 4px;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 130px;
}
.dept-chip .pi { flex-shrink: 0; font-size: 0.7rem; }

.due-cell {
  font-size: 0.75rem; font-weight: 600; display: flex; align-items: center; gap: 4px;
  white-space: nowrap;
}
.due-cell .pi { font-size: 0.7rem; }
.due-overdue { color: #dc2626; }
.due-soon { color: #ea580c; }
.due-ok { color: #16a34a; }
.due-completed { color: #94a3b8; }
.no-data { color: #cbd5e1; font-size: 0.8rem; }

/* ── SOP ────────────────────────────────────────────── */
.sop-search-row { margin-bottom: 0.75rem; }
.sop-search-wrap {
  position: relative; display: flex; align-items: center;
}
.sop-search-icon { position: absolute; left: 12px; color: var(--one-muted); font-size: 0.85rem; }
.sop-search-input {
  width: 100%; padding: 9px 36px 9px 34px;
  border-radius: 10px; border: 1px solid var(--one-rule);
  background: #f8fafc; color: #1e293b;
  font-size: 0.85rem; outline: none; transition: border-color 0.2s;
}
.sop-search-input::placeholder { color: #94a3b8; }
.sop-search-input:focus { border-color: #5eead4; background: #ffffff; }
.sop-clear { position: absolute; right: 10px; background: none; border: none; color: #94a3b8; cursor: pointer; padding: 4px; }
.sop-clear:hover { color: var(--one-muted); }

.category-pills { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 1rem; }
.cat-pill {
  font-size: 0.72rem; font-weight: 600; padding: 4px 12px; border-radius: 20px;
  border: 1px solid var(--one-rule); background: #f8fafc;
  color: var(--one-muted); cursor: pointer; transition: all 0.15s;
}
.cat-pill:hover { color: #374151; background: var(--one-rule-soft); border-color: #d1d5db; }
.cat-pill.active { background: #ccfbf1; border-color: #5eead4; color: #0d9488; }

.sop-list { display: flex; flex-direction: column; gap: 8px; }
.sop-item {
  display: flex; align-items: flex-start; gap: 12px; padding: 10px 12px;
  border-radius: 10px; background: #f8fafc;
  border: 1px solid var(--one-rule); transition: border-color 0.15s, box-shadow 0.15s;
}
.sop-item:hover { border-color: #5eead4; box-shadow: 0 1px 4px rgba(13,148,136,0.08); }
.sop-icon { flex-shrink: 0; margin-top: 2px; }
.sop-info { flex: 1; min-width: 0; }
.sop-title { font-size: 0.82rem; font-weight: 600; color: #1e293b; }
.sop-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 3px; }
.sop-cat-badge {
  font-size: 0.65rem; font-weight: 700; padding: 1px 6px; border-radius: 4px;
  background: #ede9fe; color: #6d28d9;
}
.sop-desc {
  font-size: 0.75rem; color: var(--one-muted); margin-top: 4px;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.sop-download {
  flex-shrink: 0; width: 30px; height: 30px; border-radius: 8px;
  background: #f0fdfa; border: 1px solid #5eead4;
  color: #0d9488; display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.sop-download:hover { background: #ccfbf1; border-color: #2dd4bf; }

/* ── Empty State ────────────────────────────────────── */
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 2rem; text-align: center;
}

/* ── Module Loading Overlay ─────────────────────────── */
.module-loading-overlay {
  position: fixed; inset: 0; z-index: 999;
  background: rgba(241,245,249,0.8); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}

/* ── Buttons ────────────────────────────────────────── */
.btn-ghost {
  font-size: 0.8rem; padding: 6px 14px; border-radius: 8px;
  border: 1px solid var(--one-rule); background: #f8fafc;
  color: var(--one-muted); cursor: pointer; display: inline-flex; align-items: center; transition: all 0.15s;
}
.btn-ghost:hover { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }

/* ── Scrollbar ──────────────────────────────────────── */
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: var(--one-rule-soft); }
::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #5eead4; }

/* ── Today's Pulse Strip ────────────────────────────── */
.pulse-strip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid var(--one-rule);
  border-radius: 12px;
  padding: 0.625rem 1rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.pulse-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-right: 0.25rem;
  white-space: nowrap;
}
.pulse-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  white-space: nowrap;
}
.pulse-chip-count {
  font-weight: 700;
  font-size: 0.75rem;
  background: rgba(255,255,255,0.5);
  border-radius: 10px;
  padding: 1px 6px;
}
.pulse-chip.birthday        { background: #fdf4ff; color: #9333ea; border-color: #e9d5ff; }
.pulse-chip.birthday:hover  { background: #f3e8ff; border-color: #c084fc; }
.pulse-chip.tasks-due       { background: #fff7ed; color: #c2410c; border-color: #fed7aa; }
.pulse-chip.tasks-due:hover { background: #ffedd5; border-color: #fb923c; }
.pulse-chip.circulars       { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.pulse-chip.circulars:hover { background: #dbeafe; border-color: #93c5fd; }
.pulse-chip.news            { background: #f0fdfa; color: #0d9488; border-color: #99f6e4; }
.pulse-chip.news:hover      { background: #ccfbf1; border-color: #2dd4bf; }
.pulse-chip.sop             { background: #f5f3ff; color: #6d28d9; border-color: #ddd6fe; }
.pulse-chip.sop:hover       { background: #ede9fe; border-color: #a78bfa; }

/* ── Footer ─────────────────────────────────────── */
.dash-footer {
  position: sticky;
  bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
  padding: 10px 16px;
  font-size: 0.78rem;
  color: #94a3b8;
  border-top: 1px solid rgba(255,255,255,0.15);
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(12px);
  letter-spacing: 0.02em;
}
.dash-footer strong { color: #0d9488; font-weight: 600; }
.footer-sep { color: #334155; margin: 0 10px; }
.footer-iitm { display: flex; align-items: center; gap: 5px; color: #94a3b8; }
.footer-iitm strong { color: #7dd3fc; font-weight: 600; }
.footer-iitm .pi { font-size: 0.75rem; color: #7dd3fc; }
</style>
